import { useRef, useState } from "react";
import { X, Minus, Plus, Truck, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore, type CartItem } from "@/store/cartStore";
import { formatPrice } from "@/lib/utils";
import { CartRecommendations } from "@/components/CartRecommendations";

const FREE_SHIPPING_THRESHOLD = 500;

interface MobileResizableCartProps {
  items: CartItem[];
  cartDir: 'rtl' | 'ltr';
  cartT: any;
  hasFreeShipping: boolean;
  remaining: number;
  animatedProgress: number;
  removeItem: (key: string) => void;
  updateQuantity: (key: string, qty: number) => void;
  FONT_IMAGES: Record<string, string>;
  ICON_IMAGES: Record<string, string>;
}

export const MobileResizableCart = ({
  items,
  cartDir,
  cartT,
  hasFreeShipping,
  remaining,
  animatedProgress,
  removeItem,
  updateQuantity,
  FONT_IMAGES,
  ICON_IMAGES,
}: MobileResizableCartProps) => {
  const [cartHeightVh, setCartHeightVh] = useState(35);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  const onDragStart = (clientY: number) => {
    dragRef.current = { startY: clientY, startH: cartHeightVh };
  };

  const onDragMove = (clientY: number) => {
    if (!dragRef.current) return;
    const delta = clientY - dragRef.current.startY;
    const vhDelta = (delta / window.innerHeight) * 100;
    setCartHeightVh(Math.max(10, Math.min(65, dragRef.current.startH + vhDelta)));
  };

  const onDragEnd = () => {
    dragRef.current = null;
  };

  return (
    <div className="flex md:hidden flex-col flex-1 min-h-0 overflow-hidden">
      {/* Shipping bar */}
      <div className="flex-shrink-0 mb-2 p-3 bg-primary/5 border border-primary/10 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          {hasFreeShipping ? (
            <Gift className="h-4 w-4 text-primary flex-shrink-0" />
          ) : (
            <Truck className="h-4 w-4 text-primary/70 flex-shrink-0" />
          )}
          <span className="text-xs font-medium text-foreground">
            {hasFreeShipping ? cartT.freeShippingAchieved : cartT.freeShippingRemaining(remaining)}
          </span>
        </div>
        <div className="relative h-2 bg-primary/10 rounded-full overflow-hidden">
          <div
            className={`absolute inset-y-0 rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-primary/70 to-primary ${cartDir === 'rtl' ? 'right-0' : 'left-0'}`}
            style={{ width: `${animatedProgress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
          <span>₪0</span>
          <span>₪{FREE_SHIPPING_THRESHOLD}</span>
        </div>
      </div>

      {/* Cart items - resizable height */}
      <div
        className="overflow-y-auto space-y-3 flex-shrink-0 cart-app-scrollbar"
        style={{ maxHeight: `${cartHeightVh}vh`, overscrollBehavior: 'contain' }}
      >
        {items.map((item, index) => {
          const displayImage = item.variantImage?.url || item.product.images?.edges?.[0]?.node.url;
          const displayAlt = item.variantImage?.altText || item.product.title;
          const itemKey = useCartStore.getState().getItemKey(item);
          return (
            <div key={itemKey} className="border-b border-border/50 pb-3 animate-fade-in-item opacity-0" style={{ animationDelay: `${index * 80}ms` }}>
              <div className="flex gap-3">
                <div className="w-14 h-14 bg-muted overflow-hidden flex-shrink-0 rounded">
                  {displayImage && <img src={displayImage} alt={displayAlt} loading="lazy" className="w-full h-full object-cover" />}
                </div>
                <div className={`flex-1 min-w-0 ${cartDir === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-xs leading-tight truncate">{item.product.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.variantTitle}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 text-muted-foreground/50 hover:text-destructive hover:bg-transparent" onClick={() => removeItem(itemKey)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {(() => {
                    const ICON_PRICE = 60;
                    const basePrice = parseFloat(item.price.amount);
                    const iconsPrice = (item.icons?.length || 0) * ICON_PRICE;
                    const itemTotalPrice = basePrice + iconsPrice;
                    return (
                      <div className="mt-1">
                        <p className="text-primary font-semibold text-xs">{formatPrice(itemTotalPrice.toString(), item.price.currencyCode)}</p>
                        {item.icons && item.icons.length > 0 && (
                          <p className="text-xs text-muted-foreground">{cartT.includesIcons(item.icons.length, iconsPrice)}</p>
                        )}
                      </div>
                    );
                  })()}
                  <div className={`flex items-center gap-1.5 mt-1.5 ${cartDir === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                    <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(itemKey, item.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                    <span className="text-sm w-6 text-center">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(itemKey, item.quantity - 1)}><Minus className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
              {(item.engraving || (item.icons && item.icons.length > 0)) && (
                <div className="mt-2 py-2 px-3 border border-primary/20 bg-primary/[0.02]">
                  <div className="flex items-center justify-center gap-3 text-xs flex-wrap">
                    {item.engraving && (
                      <>
                        {(item.engraving.texts && item.engraving.texts.length > 0) ? (
                          item.engraving.texts.map((text, idx) => (
                            <span key={idx} className="flex items-center gap-1">
                              <span className="text-muted-foreground">{item.engraving!.texts.length > 1 ? cartT.engravingN(idx + 1) : cartT.engraving}</span>
                              <span className="text-foreground">{text}</span>
                            </span>
                          ))
                        ) : (item.engraving as any).text ? (
                          <span className="flex items-center gap-1">
                            <span className="text-muted-foreground">{cartT.engraving}</span>
                            <span className="text-foreground">{(item.engraving as any).text}</span>
                          </span>
                        ) : null}
                        <span className="text-primary/30">|</span>
                        <span className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">{cartT.font}</span>
                          {FONT_IMAGES[item.engraving.font] ? (
                            <img src={FONT_IMAGES[item.engraving.font]} alt={item.engraving.font} className="h-5 w-auto object-contain" />
                          ) : (
                            <span className="text-foreground">{item.engraving.font}</span>
                          )}
                        </span>
                      </>
                    )}
                    {item.icons && item.icons.length > 0 && (
                      <>
                        {item.engraving && <span className="text-primary/30">|</span>}
                        <span className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">{item.icons.length === 1 ? cartT.iconSingular : cartT.iconPlural}</span>
                          <span className="flex items-center gap-1">
                            {item.icons.map((iconId, idx) => (
                              ICON_IMAGES[iconId] ? (
                                <img key={idx} src={ICON_IMAGES[iconId]} alt={iconId} className="h-5 w-5 object-contain" />
                              ) : (
                                <span key={idx} className="text-foreground">{iconId}</span>
                              )
                            ))}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Drag handle to resize */}
      <div
        className="flex-shrink-0 flex items-center justify-center py-1.5 cursor-row-resize touch-none select-none"
        onTouchStart={(e) => onDragStart(e.touches[0].clientY)}
        onTouchMove={(e) => { e.preventDefault(); onDragMove(e.touches[0].clientY); }}
        onTouchEnd={onDragEnd}
        onMouseDown={(e) => {
          onDragStart(e.clientY);
          const move = (ev: MouseEvent) => onDragMove(ev.clientY);
          const up = () => { onDragEnd(); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
          window.addEventListener('mousemove', move);
          window.addEventListener('mouseup', up);
        }}
      >
        <div className="w-10 h-1 rounded-full bg-border" />
      </div>

      {/* Recommendations - takes remaining space */}
      <div className="flex-1 overflow-y-auto min-h-0 cart-app-scrollbar" style={{ overscrollBehavior: 'contain' }}>
        <CartRecommendations cartItems={items} title={cartT.recommendations} />
      </div>
    </div>
  );
};
